import * as z from "zod";
import { isoDate } from "./common";
import { TABLE_ORDER_STATUSES } from "./vendor-show";

export const EVENT_TABLE_STATUSES = ["AVAILABLE", "HELD", "SOLD"] as const;

export const EventTable = z.object({
  id: z.string(),
  /** Unique across the show. */
  number: z.number(),
  /** The kind's prefix and the table's number within the kind: A-2. */
  label: z.string(),
  /** HELD while a shop pays for it; a hold that lapsed reads as AVAILABLE. */
  status: z.enum(EVENT_TABLE_STATUSES),
  soldAt: isoDate.nullable(),
  /** The booth holding a taken table, and its shop. */
  booth: z
    .object({
      id: z.string(),
      store: z.object({ id: z.string(), name: z.string().nullable() }),
    })
    .nullable(),
  /** The payment for a SOLD table: REFUNDED once it went back, FAILED while a refund is owed. Null for any other. */
  order: z
    .object({
      id: z.string(),
      status: z.enum(TABLE_ORDER_STATUSES),
      /** What the shop paid, fees included, in `currency`'s minor units. */
      amountCents: z.number(),
      currency: z.string(),
      /** The email a reservation was bought for, until a shop redeems it; null for a shop's table. */
      reservedFor: z.string().nullable(),
    })
    .nullable(),
});
export type EventTable = z.output<typeof EventTable>;

/** A kind of table at a hosted show, with every table of it. */
export const EventTableKind = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  /** The host's note for the shop that pays for a table of the kind, shown to that shop only once paid. */
  privateDescription: z.string().nullable(),
  /** In `currency`'s minor units. */
  priceCents: z.number(),
  currency: z.string(),
  position: z.number(),
  /** The letters before each table's number in the kind (A-1, A-2), unique at the show. */
  prefix: z.string(),
  tables: z.array(EventTable),
});
export type EventTableKind = z.output<typeof EventTableKind>;

export const EventTableKinds = z.object({ kinds: z.array(EventTableKind) });
export type EventTableKinds = z.output<typeof EventTableKinds>;

/** A kind of table and how many of it; the price is in the host shop's currency. */
export interface NewTableKind {
  name: string;
  description?: string;
  privateDescription?: string;
  priceCents: number;
  quantity: number;
  /** One to three letters, unique at the show; unless given, the next letter the show has not used. */
  prefix?: string;
}

/** Only what is set changes; `null` clears a description. */
export interface TableKindUpdate {
  name?: string;
  description?: string | null;
  privateDescription?: string | null;
  priceCents?: number;
  /** More tables take the show's next numbers; fewer drop the highest-numbered ones still for sale. */
  quantity?: number;
  /** New letters for the kind's labels; the tables keep their numbers within it. */
  prefix?: string;
}
